import config from '../config';

// Use a data URI as default instead of a file that might not exist
const DEFAULT_AVATAR_DATA_URI = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"%3E%3Ccircle cx="50" cy="50" r="50" fill="%23e9ecef"/%3E%3Ctext x="50" y="50" text-anchor="middle" dy=".3em" fill="%236c757d" font-family="Arial, sans-serif" font-size="40" font-weight="bold"%3E%3F%3C/text%3E%3C/svg%3E';

/**
 * Safely constructs a full image URL from a potentially partial or invalid image URL
 * @param imageUrl - The image URL from the database (could be relative, absolute, or invalid)
 * @param defaultImage - Optional default image to use if imageUrl is invalid
 * @returns A valid full URL for the image
 */
export function getValidImageUrl(imageUrl: string | null | undefined, defaultImage: string = DEFAULT_AVATAR_DATA_URI): string {
    // If no image URL provided, return default data URI
    if (!imageUrl) {
        return defaultImage;
    }

    // Trim whitespace
    const trimmedUrl = imageUrl.trim();

    // Check if it's just a numeric ID (invalid format)
    if (/^\d+$/.test(trimmedUrl)) {
        console.warn('Invalid image URL detected (numeric only):', trimmedUrl);
        return defaultImage;
    }

    // Check if it's already a full URL (http/https)
    if (trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://')) {
        return trimmedUrl;
    }

    // Check if it's a relative path starting with /
    if (trimmedUrl.startsWith('/')) {
        // Ensure we don't double up on slashes
        const cleanPath = trimmedUrl.startsWith('/uploads/') ? trimmedUrl : trimmedUrl;
        return `${config.api.BASE_URL}${cleanPath}`;
    }

    // Check if it's a relative path without leading slash (e.g., "uploads/avatars/...")
    if (trimmedUrl.startsWith('uploads/')) {
        return `${config.api.BASE_URL}/${trimmedUrl}`;
    }

    // Check for legacy buyout_attachments path format
    if (trimmedUrl.startsWith('buyout_attachments/')) {
        // Map old path to actual location in documents folder
        return `${config.api.BASE_URL}/uploads/documents/${trimmedUrl}`;
    }

    // If it doesn't match any valid pattern, return default
    console.warn('Invalid image URL format:', trimmedUrl);
    return defaultImage;
}

/**
 * Gets the user avatar URL, handling various formats and fallbacks
 * @param user - User object with potential image_url or image properties
 * @returns A valid full URL for the user's avatar
 */
export function getUserAvatarUrl(user: { image_url?: string; image?: string } | null): string {
    if (!user) {
        return getValidImageUrl(null);
    }

    // Priority: image_url > image > default
    if (user.image_url) {
        return getValidImageUrl(user.image_url);
    }

    if (user.image) {
        return getValidImageUrl(`/uploads/avatars/${user.image}`);
    }

    return getValidImageUrl(null);
}